import { createRoot } from 'react-dom/client';
import App from '@/app/App.jsx';
import '@/styles/tailwind.css';
import { AppProviders } from './providers';
import { initPerformanceObserver } from './perf';
import { initGoogleAdsTag } from '@/lib/analytics/googleAds';

function scheduleIdle(fn, timeoutMs = 2000) {
  if (typeof window === 'undefined') return;
  const run = () => {
    try {
      fn();
    } catch (_) {
      // never block app startup
    }
  };
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: timeoutMs });
  } else {
    window.setTimeout(run, 750);
  }
}

function scheduleThirdPartyInit(fn, { directoryDelayMs = 6000 } = {}) {
  if (typeof window === 'undefined') return;
  const isDirectoryRoute = window.location.pathname.startsWith('/directory');
  if (!isDirectoryRoute) {
    scheduleIdle(fn);
    return;
  }

  let didRun = false;
  const events = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
  const runOnce = () => {
    if (didRun) return;
    didRun = true;
    events.forEach(event => window.removeEventListener(event, runOnce));
    scheduleIdle(fn, 2500);
  };

  events.forEach(event =>
    window.addEventListener(event, runOnce, { passive: true, once: true })
  );
  window.setTimeout(runOnce, directoryDelayMs);
}

// Initialize Sentry after first paint to reduce startup contention.
scheduleThirdPartyInit(() => {
  import('../sentry.js')
    .then(m => m.initSentry?.())
    .catch(() => {});
});

// Initialize Analytics during idle time to reduce main-thread + network contention during LCP.
scheduleThirdPartyInit(() => {
  initGoogleAdsTag();
});

// Initialize lightweight performance observer
initPerformanceObserver();

// Global handler to suppress non-Error "Timeout" rejections (common in 3rd party libs like reCAPTCHA)
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', event => {
    const reason = event.reason;
    
    // Check for Google API "u[v] is not a function" in rejections
    const isGoogleError = 
      reason?.message?.includes('u[v] is not a function') || 
      (typeof reason === 'string' && reason.includes('u[v] is not a function'));

    if (isGoogleError) {
      event.preventDefault();
      return;
    }

    // Check for CefSharp/bot errors
    const reasonString = typeof reason === 'string' ? reason : (reason?.message || reason?.toString?.() || '');
    if (
      reasonString.includes('Object Not Found Matching Id') &&
      reasonString.includes('MethodName:update')
    ) {
      event.preventDefault();
      return;
    }

    // Check for "Timeout (u)" or "Timeout" string rejections
    const isTimeout =
      reason === 'Timeout (u)' ||
      reason === 'Timeout (b)' ||
      reason === 'Timeout' ||
      (reason &&
        typeof reason.toString === 'function' &&
        (reason.toString() === 'Timeout (u)' ||
          reason.toString() === 'Timeout (b)' ||
          reason.toString() === 'Timeout'));

    // iOS Safari occasionally loses IndexedDB during heavy third-party startup work (gtag, etc).
    // This frequently surfaces as an unhandled promise rejection; it is not actionable for users.
    const isIndexedDbServerLost =
      reasonString.includes('Connection to Indexed Database server lost') ||
      reasonString.includes('Indexed Database server lost') ||
      reasonString.includes('Object store cannot be found in the database') ||
      reasonString.includes('Error looking up record in object store by key range');

    if (isTimeout) {
      // Prevent the default handling (printing to console)
      event.preventDefault();
      return;
    }

    if (isIndexedDbServerLost) {
      event.preventDefault();
    }
  });

  // Global error handler to suppress known 3rd party script errors
  window.addEventListener('error', event => {
    // Suppress "u[v] is not a function" from Google API (gapi) loader
    // This occurs on some older Android/Chrome versions
    const msg = event.message || event.error?.message || '';
    // Suppress "elementFromPoint(...).click()" error from ReCAPTCHA/Ads on iOS
    // This seems to be a bug in the Google script using document coordinates instead of viewport coordinates
    if (
      msg.includes('document.elementFromPoint') &&
      msg.includes('click') &&
      (msg.includes('null is not an object') || msg.includes('Cannot read properties of null'))
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (msg.includes('u[v] is not a function')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  });
}

// Prefetch critical data removed - landing page is now static
// const prefetchCriticalData = () => {
//   // Removed featured advisors prefetch - no longer needed
//   // queryClient.prefetchQuery({
//   //   queryKey: ['featured-advisors', 6],
//   //   queryFn: async () => {
//   //     const response = await fetch('/api/advisors/featured');
//   //     if (!response.ok) throw new Error('Network response was not ok');
//   //     return response.json();
//   //   },
//   //   staleTime: 10 * 60 * 1000,
//   // });

//   // Search suggestions prefetch removed - not needed for static landing page
//   queryClient.prefetchQuery({
//     queryKey: ['search-suggestions-popular'],
//     queryFn: async () => {
//       return [
//         'Financial Planning',
//         'Retirement Planning',
//         'Investment Management',
//         'Estate Planning'
//       ];
//     },
//     staleTime: 30 * 60 * 1000,
//   });
// };

// Preload critical data removed - landing page is static now
// if (typeof window !== 'undefined') {
//   setTimeout(prefetchCriticalData, 100);
// }

createRoot(document.getElementById('root')).render(
  <AppProviders>
    <App />
  </AppProviders>
);
